/*
 * Pipeline Theme — custom Javascript
 */

(function () {
  const variantCache = new Map();
  const loadingPromises = new Map();

  function selectSwatch(swatchLink) {
    const swatchList = swatchLink.closest('.custom-color-swatches');
    if (!swatchList) return;

    swatchList.querySelectorAll('.custom-color-swatches__item--current').forEach((item) => {
      item.classList.remove('custom-color-swatches__item--current');
    });
    swatchList.querySelectorAll('.custom-color-swatches__link').forEach((link) => {
      link.removeAttribute('aria-current');
      link.removeAttribute('aria-selected');
    });

    const item = swatchLink.closest('.custom-color-swatches__item');
    if (item) item.classList.add('custom-color-swatches__item--current');
    swatchLink.setAttribute('aria-current', 'true');
    swatchLink.setAttribute('aria-selected', 'true');
  }

  function updateGridItemFromSwatch(swatchLink) {
    const gridItem = swatchLink.closest('product-grid-item');
    if (!gridItem) return;

    const title = swatchLink.getAttribute('data-product-title');
    const url = swatchLink.getAttribute('data-product-url') || swatchLink.getAttribute('href');

    if (title) {
      const titleEl = gridItem.querySelector('[data-grid-product-title]');
      const hiddenTitleEl = gridItem.querySelector('[data-grid-product-title-hidden]');
      if (titleEl) titleEl.textContent = title;
      if (hiddenTitleEl) hiddenTitleEl.textContent = title;
      gridItem.setAttribute('aria-label', title);
    }

    if (url) {
      gridItem.querySelectorAll('[data-grid-link]').forEach((link) => {
        link.setAttribute('href', url);
        if (title) link.setAttribute('aria-label', title);
      });
    }

    selectSwatch(swatchLink);
    requestAnimationFrame(() => selectSwatch(swatchLink));
  }

  function hideVariant(el) {
    el.setAttribute('hidden', '');
    el.hidden = true;
    el.style.display = 'none';
  }

  function revealVariant(el) {
    el.removeAttribute('hidden');
    el.hidden = false;
    el.style.display = '';
  }

  function dedupeGridVariants(gridItem) {
    const seen = new Set();
    gridItem.querySelectorAll(':scope > product-grid-item-variant[data-grid-item-variant]').forEach((el) => {
      const id = el.getAttribute('data-grid-item-variant');
      if (!id) return;
      if (seen.has(id)) {
        el.remove();
      } else {
        seen.add(id);
      }
    });
  }

  function showGridVariant(gridItem, variantId) {
    dedupeGridVariants(gridItem);

    const variants = gridItem.querySelectorAll(':scope > product-grid-item-variant[data-grid-item-variant]');
    let shown = null;

    variants.forEach((el) => {
      if (el.getAttribute('data-grid-item-variant') === String(variantId)) {
        shown = el;
        revealVariant(el);
      } else {
        hideVariant(el);
      }
    });

    // If ids mismatched on a fetched card, fall back to last injected unhidden candidate
    if (!shown && variants.length) {
      variants.forEach((el, index) => {
        if (index === variants.length - 1) {
          revealVariant(el);
        } else {
          hideVariant(el);
        }
      });
    }
  }

  function getRootUrl() {
    const root = (window.theme && window.theme.routes && window.theme.routes.root_url) || '/';
    return root.endsWith('/') ? root : root + '/';
  }

  function loadKey(gridItem, variantId) {
    return String(gridItem.getAttribute('data-item-id') || gridItem) + ':' + String(variantId);
  }

  async function ensureGridVariantLoaded(gridItem, swatchLink) {
    const variantId = swatchLink.getAttribute('data-grid-item-variant');
    if (!variantId) return null;

    dedupeGridVariants(gridItem);

    const existing = gridItem.querySelector(
      `:scope > product-grid-item-variant[data-grid-item-variant="${variantId}"]`
    );
    if (existing) return existing;

    const key = loadKey(gridItem, variantId);
    if (loadingPromises.has(key)) {
      await loadingPromises.get(key);
      return gridItem.querySelector(
        `:scope > product-grid-item-variant[data-grid-item-variant="${variantId}"]`
      );
    }

    const handle = swatchLink.getAttribute('data-product-handle');
    if (!handle) return null;

    const promise = (async () => {
      // Re-check after awaiting any yield
      const again = gridItem.querySelector(
        `:scope > product-grid-item-variant[data-grid-item-variant="${variantId}"]`
      );
      if (again) return again;

      const cacheKey = handle + ':' + variantId;
      let variant;

      if (variantCache.has(cacheKey)) {
        variant = variantCache.get(cacheKey).cloneNode(true);
      } else {
        const url = `${getRootUrl()}products/${handle}?section_id=api-product-grid-item`;
        const response = await fetch(url);
        if (!response.ok) return null;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        variant = doc.querySelector('product-grid-item-variant');
        if (!variant) return null;

        // Normalize id to the swatch's variant id so show/hide stays in sync
        variant.setAttribute('data-grid-item-variant', variantId);
        variantCache.set(cacheKey, variant.cloneNode(true));
      }

      // Final guard against parallel inserts
      const latest = gridItem.querySelector(
        `:scope > product-grid-item-variant[data-grid-item-variant="${variantId}"]`
      );
      if (latest) return latest;

      insertGridVariant(gridItem, variant);
      return variant;
    })();

    loadingPromises.set(key, promise);
    try {
      return await promise;
    } finally {
      loadingPromises.delete(key);
    }
  }

  function insertGridVariant(gridItem, variantEl) {
    // Keep prefetched colors hidden until the swatch is actually selected
    hideVariant(variantEl);
    const info = gridItem.querySelector('.product__grid__info');
    if (info) {
      gridItem.insertBefore(variantEl, info);
    } else {
      gridItem.appendChild(variantEl);
    }
  }

  function isSizeOptionName(name) {
    return /size/i.test(String(name || ''));
  }

  function getOptionNameFromInput(input) {
    const nameAttr = input.getAttribute('name') || '';
    const match = nameAttr.match(/^options\[(.+)\]$/);
    if (match) return match[1];

    const wrapper = input.closest('[data-select-label]');
    if (wrapper) return wrapper.getAttribute('data-select-label');

    return '';
  }

  function getSelectedSize() {
    const checkedRadios = document.querySelectorAll(
      'input[type="radio"][name^="options["]:checked'
    );
    for (const radio of checkedRadios) {
      if (isSizeOptionName(getOptionNameFromInput(radio))) {
        return radio.value;
      }
    }

    const selects = document.querySelectorAll('select[name^="options["]');
    for (const select of selects) {
      if (isSizeOptionName(getOptionNameFromInput(select))) {
        return select.value;
      }
    }

    return null;
  }

  function updateProductColorSwatchesForSize(size) {
    const swatches = document.querySelectorAll(
      '.custom-color-swatches--product .custom-color-swatches__item'
    );
    if (!swatches.length) return;

    swatches.forEach((item) => {
      const link = item.querySelector('.custom-color-swatches__link');
      if (!link) return;

      const whollyUnavailable = link.getAttribute('data-product-available') === 'false';
      const availableSizes = link.getAttribute('data-available-sizes') || '';
      let soldOut = whollyUnavailable;

      if (!soldOut && size) {
        const token = '|' + size + '|';
        soldOut = availableSizes.indexOf(token) === -1;
      }

      item.classList.toggle('sold-out', soldOut);
      item.classList.toggle('custom-color-swatches__item--sold-out', soldOut);

      if (size) {
        try {
          const sizeUrls = JSON.parse(link.getAttribute('data-size-urls') || '{}');
          if (sizeUrls[size]) {
            link.setAttribute('href', sizeUrls[size]);
          }
        } catch (e) {
          // ignore invalid JSON
        }
      }
    });
  }

  // Prefetch color card media on hover so click feels instant
  document.addEventListener(
    'pointerenter',
    function (event) {
      const swatchLink = event.target.closest(
        'a.custom-color-swatches__link[data-grid-item-variant][data-product-handle]'
      );
      if (!swatchLink) return;
      const gridItem = swatchLink.closest('product-grid-item');
      if (!gridItem) return;
      ensureGridVariantLoaded(gridItem, swatchLink);
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      const customColorLink = event.target.closest(
        'a.custom-color-swatches__link[data-grid-item-variant][data-product-handle]'
      );

      if (customColorLink) {
        const gridItem = customColorLink.closest('product-grid-item');
        if (!gridItem) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const variantId = customColorLink.getAttribute('data-grid-item-variant');
        const requestId = String(Date.now()) + Math.random();
        gridItem.dataset.activeSwatchRequest = requestId;

        ensureGridVariantLoaded(gridItem, customColorLink).then((variantEl) => {
          // Ignore stale responses if user clicked another swatch meanwhile
          if (gridItem.dataset.activeSwatchRequest !== requestId) return;
          if (variantEl && variantId) {
            showGridVariant(gridItem, variantId);
          }
          updateGridItemFromSwatch(customColorLink);
        });
        return;
      }

      const siblingLink = event.target.closest('a.siblings__link[data-product-title]');
      if (siblingLink) {
        updateGridItemFromSwatch(siblingLink);
      }
    },
    true
  );

  function findMissingSizeWrapper(form) {
    // form.id is shadowed by the <input name="id"> inside product forms
    const rawId = form && form.getAttribute('id');
    if (!rawId) return null;
    const formId = window.CSS && CSS.escape ? CSS.escape(rawId) : rawId;
    const wrappers = document.querySelectorAll('[data-size-required]');

    for (const wrapper of wrappers) {
      const inputs = wrapper.querySelectorAll(`[name^="options["][form="${formId}"]`);
      if (!inputs.length) continue;

      const hasSize = Array.from(inputs).some((input) =>
        input.type === 'radio' ? input.checked : input.value !== ''
      );
      if (!hasSize) return wrapper;
    }
    return null;
  }

  function showSizeError(wrapper) {
    const bar = wrapper.querySelector('[data-size-error]');
    wrapper.classList.add('size-required--error');
    if (bar) bar.hidden = false;
    wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function hideSizeError(wrapper) {
    if (!wrapper) return;
    const bar = wrapper.querySelector('[data-size-error]');
    wrapper.classList.remove('size-required--error');
    if (bar) bar.hidden = true;
  }

  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (!form || !form.matches || !form.matches('[data-product-form]')) return;

      const wrapper = findMissingSizeWrapper(form);
      if (!wrapper) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      showSizeError(wrapper);
    },
    true
  );

  // Dynamic checkout ("Buy it now") bypasses the form submit
  document.addEventListener(
    'click',
    function (event) {
      const quickBuy = event.target.closest && event.target.closest('.product__submit__quick');
      if (!quickBuy) return;

      const form = quickBuy.closest('form[data-product-form]');
      const wrapper = findMissingSizeWrapper(form);
      if (!wrapper) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      showSizeError(wrapper);
    },
    true
  );

  document.addEventListener('change', function (event) {
    const input = event.target;
    if (!input || !input.name || input.name.indexOf('options[') !== 0) return;
    if (!isSizeOptionName(getOptionNameFromInput(input))) return;
    hideSizeError(input.closest('[data-size-required]'));
    updateProductColorSwatchesForSize(input.value);
  });

  document.addEventListener('DOMContentLoaded', function () {
    updateProductColorSwatchesForSize(getSelectedSize());
  });

  if (document.readyState !== 'loading') {
    updateProductColorSwatchesForSize(getSelectedSize());
  }
})();
